import {
  ActionTagOption,
  AuditLogCommands,
  AuditLogPaginationProps,
  AuditLogRowData,
  AuditLogTableProps,
  NotificationAreaProps,
} from '@ardoq/audit-log';
import { IconName } from '@ardoq/icons';
import {
  APIEntityType,
  ArdoqId,
  AuditLogAction,
  AuditLogEntityType,
  PageState,
} from '@ardoq/api-types';
import { SelectOption } from '@ardoq/select';
import { FilterSections, FIXED_DATE_RANGES, LoadingState } from './consts';
import { FilterBarValue } from '@ardoq/snowflakes';
import { ArdoqError, Result } from '@ardoq/common-helpers';
import { Locale } from '@ardoq/locale';
import { I18nLocale } from '@ardoq/i18n';

export type DateRange = {
  before: Date | null;
  since: Date | null;
};

export type FilterState = {
  workspaces: SelectOption<ArdoqId>[];
  actions: AuditLogAction[];
  users: ArdoqId[];
  excludeServiceAccounts: boolean;
  name: string;
  entityTypes: Record<AuditLogEntityType, Array<SelectOption<string>> | null>;
};

type APIEntityTypeFilter = Partial<
  Record<AuditLogEntityType, Array<string> | null>
>;

type APIWorkspaceFilter = ArdoqId[];

// TODO [audit log improvements] simplify props and move callbacks to commands
export type WorkspaceSectionProps = {
  selected: FilterState['workspaces'];
  disabled?: boolean;
  loadOptions: LoadSuggestionOptionsFn;
  onValueChange: (value: FilterState['workspaces']) => void;
  i18nLocale: I18nLocale;
};

export type UsersSectionProps = {
  selected: FilterState['users'];
  options: SelectOption<string>[];
  onValueChange: (value: FilterState['users']) => void;
  excludeServiceAccounts: FilterState['excludeServiceAccounts'];
  onExcludeServiceAccountsChange: (
    value: FilterState['excludeServiceAccounts']
  ) => void;
  i18nLocale: I18nLocale;
};

export type DateRangeSectionProps = {
  selected: AuditLogDateRange;
  hasFullAuditLogHistoryFeature: boolean;
  onValueChange: (value: AuditLogDateRange) => void;
  locale: Locale;
  i18nLocale: I18nLocale;
};

export type ActionsSectionProps = {
  selected: FilterState['actions'];
  options: ActionTagOption[];
  onValueChange: (value: FilterState['actions']) => void;
  i18nLocale: I18nLocale;
};

export type ChangeTypeSectionProps = {
  selected: FilterState['entityTypes'];
  onCheckboxValueChange: (value: Array<AuditLogEntityType>) => void;
  onSelectValueChange: (
    value: Array<SelectOption<string> | string> | null,
    entityType: AuditLogEntityType
  ) => void;
  loadComponentOptions: LoadSuggestionOptionsFn;
  loadReferenceOptions: LoadSuggestionOptionsFn;
  loadComponentTypeOptions: LoadSuggestionOptionsFn;
  loadReferenceTypeOptions: LoadSuggestionOptionsFn;
  loadFieldOptions: LoadSuggestionOptionsFn;
  loadWorkspaceOptions: LoadSuggestionOptionsFn;
  i18nLocale: I18nLocale;
};

// TODO [audit log improvements] simplify and turn into
/*
type FilterSectionProps = {
  name: string;
  workspaceSectionProps: WorkspaceSectionProps;
  usersSectionProps: UsersSectionProps;
  dateRangeSectionProps: DateRangeSectionProps;
  actionsSectionProps: ActionsSectionProps;
  changeTypeSectionProps: ChangeTypeSectionProps;
};
*/
export type FilterSectionProps = {
  name: string;
  hasFilterButtonFeature: boolean;
  onApplyFilters: VoidFunction;
  i18nLocale: I18nLocale;
  [FilterSections.WORKSPACES]: WorkspaceSectionProps;
  [FilterSections.USERS]: UsersSectionProps;
  [FilterSections.ACTIONS]: ActionsSectionProps;
  [FilterSections.CHANGE_TYPE]: ChangeTypeSectionProps;
};

export type ChangeTypeFilterConfig = {
  entityType: AuditLogEntityType;
  loadOptions: LoadSuggestionOptionsFn;
};

export type ChangeTypeFilterProps = ChangeTypeFilterConfig & {
  selectedOptions: Array<SelectOption<string>> | null;
  onSelectValueChange: ChangeTypeSectionProps['onSelectValueChange'];
  i18nLocale: I18nLocale;
};

export type AuditLogRequest = {
  pageNumber?: number;
  pageState?: PageState;
  size?: number;
  filters: Partial<Omit<FilterState, 'entityTypes' | 'workspaces'>> & {
    entityTypes: APIEntityTypeFilter;
    workspaces: APIWorkspaceFilter;
  };
  before?: Date;
  since?: Date;
};

// TODO [audit log improvements] remove redundant props once things are simplified
export type AuditLogViewModelState = Omit<
  AuditLog$State,
  | 'filters'
  | 'appliedFilters'
  | 'paginationState'
  | 'showRefreshNotification'
  | 'lastRestoreResult'
  | 'dateRange'
> & {
  filterProps: FilterSectionProps;
  commands: AuditLogCommands;
  searchAndFilterBarProps: {
    activeFilters: Array<{
      value: string;
      label: string;
      type: FilterSections | AuditLogEntityType;
      iconName: IconName;
    }>;
    removeFilter: (value: FilterBarValue, type: string) => void;
    clearAllFilters: VoidFunction;
    onSearch: (searchString: string) => void;
    onFilterButtonClick: VoidFunction;
    dateRangeSelectionProps: DateRangeSectionProps;
    locale: Locale;
    i18nLocale: I18nLocale;
  };
  isAuditLogEmpty: boolean;
  auditLogTableProps: AuditLogTableProps;
  auditLogPaginationProps: AuditLogPaginationProps;
  notificationAreaProps: NotificationAreaProps;
  features: {
    hasFullAuditLogHistoryFeature: boolean;
    hasApplyFiltersButtonFeature: boolean;
  };
  locale: Locale;
  i18nLocale: I18nLocale;
};

export type AuditLog$State = {
  isFilterSidebarOpen: boolean;
  filters: FilterState;
  appliedFilters: FilterState;
  dateRange: AuditLogDateRange;
  loadingState: LoadingState;
  data: Array<AuditLogRowData>;
  paginationState: {
    pageSize: number;
    pageState?: PageState;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    currentPage: number;
  };
  showRefreshNotification: boolean;
  apiErrors: ArdoqError[];
  lastRestoreResult: Result<ArdoqId>;
};

export type SetAuditLogFilterPayload =
  | Pick<FilterState, 'workspaces'>
  | Pick<FilterState, 'actions'>
  | Pick<FilterState, 'users'>
  | Pick<FilterState, 'excludeServiceAccounts'>
  | Pick<FilterState, 'name'>
  | Pick<FilterState, 'entityTypes'>;

export type FetchAuditLogDataPayload =
  | { filters: SetAuditLogFilterPayload }
  | { pageNumber: number; pageState?: PageState; size: number }
  | null;

export type AuditLogSuggestionFn = (
  name: string
) => Promise<Array<SelectOption<string>>>;

type LoadSuggestionOptionsFn = (
  query: string,
  callback: (options: Array<SelectOption<string>>) => void
) => Promise<Array<SelectOption<string>>> | void;

export enum DateRangeType {
  FIXED,
  CUSTOM,
}

export type FixedDateRangeValue = (typeof FIXED_DATE_RANGES)[number];

export type AuditLogDateRange =
  | {
      rangeType: DateRangeType.FIXED;
      value: FixedDateRangeValue;
    }
  | {
      rangeType: DateRangeType.CUSTOM;
      value: DateRange;
    };

export const emptyChangeTypeFilterState = {
  [APIEntityType.COMPONENT]: null,
  [APIEntityType.COMPONENT_TYPE]: null,
  [APIEntityType.REFERENCE]: null,
  [APIEntityType.REFERENCE_TYPE]: null,
  [APIEntityType.FIELD]: null,
  [APIEntityType.WORKSPACE]: null,
};

export const initialFilterState: Readonly<FilterState> = {
  workspaces: [],
  actions: [],
  users: [],
  excludeServiceAccounts: false,
  name: '',
  entityTypes: emptyChangeTypeFilterState,
};
